import {
    Box,
    Button,
    Stack,
    Typography,
    Paper,
} from "@mui/material";
import MuiAiAnswer from "../../components/Answer/MuiAiAnswer";
import MuiUserMessage from "../../components/UserMessage/MuiUserMessage";
import MuiDraftMessage from "../../components/UserMessage/MuiDraftMessage";
import { useAppStateContext } from "../../state/AppContextProvider";
import { AUTHOR_ROLE } from "./utils";
import { useEffect, useRef, useState } from "react";
import { ChatMessage, getCurrentOrchestration } from "../../common/models";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import DOMPurify from 'dompurify';
import { XSSAllowTags } from '../../constants/xssAllowTags';
import ChevronProgress from "../../components/StepperMenu/ChatStepper";
import { faExclamationTriangle, faTriangleExclamation, faWarning } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
interface StyleColor {
    bg: string;
    color: string;
}

export type NotificationStyles = {
    [key in AUTHOR_ROLE]: StyleColor;
};

// Notification background and text colors
export const notificationStyles: Partial<NotificationStyles> = {
    // [AUTHOR_ROLE.SUCCESS]: {
    //     bg: "success.light",
    //     color: "success.main",
    // },
    // [AUTHOR_ROLE.WARNING]: {
    //     bg: "warning.light",
    //     color: "warning.main",
    // },
    // [AUTHOR_ROLE.ERROR]: {
    //     bg: "error.light",
    //     color: "error.main",
    // },
    // [AUTHOR_ROLE.INFO]: {
    //     bg: "info.light",
    //     color: "info.main",
    // },
};

export interface Message {
    id: number | string,
    text: string,
    notification?: {
        type: keyof NotificationStyles,
        title: string,
        message: string,
    },
}

interface MuiChatThreadProps {
    showFooterButtons: boolean;
    retry: (question: string) => void
}

const MuiChatThread: React.FC<MuiChatThreadProps> = ({ showFooterButtons, retry }) => {
    const appStateContext = useAppStateContext()
    const { exportChat, glossary } = appStateContext;
    const chatMessageStreamEnd = useRef<HTMLDivElement>(null);
    const currentMessages = appStateContext.state.currentConversation.messages;
    const currentOrchestration = getCurrentOrchestration(appStateContext.state.currentOrchState!);
    const currentAgent = currentOrchestration?.currentAgent || null;
    const [messageUpdating, setMessageUpdating] = useState<ChatMessage | null>(null);
    const [downloadingExportType, setDownloadingExportType] = useState<string | null>(null);
    
    // State for glossary popup
    const [popup, setPopup] = useState<{
        visible: boolean;
        x: number;
        y: number;
        tag: string;
        definition: string;
    }>({
        visible: false,
        x: 0,
        y: 0,
        tag: '',
        definition: ''
    });

    // Helper function to wrap glossary terms with styled spans
    const highlightGlossaryTerms = (content: string): string => {
        if (!glossary || glossary.length === 0) {
            return content;
        }
        
        let highlightedContent = content;
        const replacedTags = new Set<string>(); // Track which tags have been replaced
        
        // Split content by lines to check for headers
        const lines = highlightedContent.split('\n');
        
        // Iterate through each glossary item
        glossary.forEach(item => {
            if (!item.tags) return;
            
            // Split the semicolon-delimited tags
            const tags = item.tags.split(';').map(tag => tag.trim()).filter(Boolean);
            
            // For each tag, find and wrap only the FIRST occurrence that's not in a header
            tags.forEach(tag => {
                if (!tag || replacedTags.has(tag.toLowerCase())) return;
                
                // Escape special regex characters
                const escapedTag = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                
                // Create a case-insensitive regex that matches the term with word boundaries
                const regex = new RegExp(`\\b${escapedTag}\\b`, 'i');
                
                // Find first occurrence not in a header or export string
                for (let i = 0; i < lines.length; i++) {
                    const line = lines[i];
                    
                    // Skip if line is a markdown header or contains "Export to PDF"
                    if (line.trim().match(/^#{1,6}\s/) || line.includes('Export to PDF:')) {
                        continue;
                    }
                    
                    // Check if this line contains the tag (but not already wrapped)
                    if (regex.test(line) && !line.includes('class="glossary-term"')) {
                        // Replace only in this line
                        lines[i] = line.replace(regex, (match) => {
                            const definition = item.definition || '';
                            // Escape HTML entities and normalize whitespace to prevent attribute breaking
                            const escapeHtml = (str: string) => str
                                .replace(/\r\n/g, ' ')  // Replace Windows newlines with space
                                .replace(/\n/g, ' ')     // Replace Unix newlines with space
                                .replace(/\r/g, ' ')     // Replace Mac newlines with space
                                .replace(/\s+/g, ' ')    // Collapse multiple spaces into one
                                .trim()                  // Remove leading/trailing whitespace
                                .replace(/&/g, '&amp;')
                                .replace(/"/g, '&quot;')
                                .replace(/'/g, '&#39;')
                                .replace(/</g, '&lt;')
                                .replace(/>/g, '&gt;');
                            
                            const escapedTag = escapeHtml(match);
                            const escapedDefinition = escapeHtml(definition);
                            return `<span class="glossary-term" data-tag="${escapedTag}" data-definition="${escapedDefinition}" style="font-weight: bold; text-decoration: underline dashed red; cursor: help;">${match}</span>`;
                        });
                        replacedTags.add(tag.toLowerCase());
                        break; // Stop after first replacement
                    }
                }
            });
        });
        
        return lines.join('\n');
    };




    // Event listeners for glossary term hover
    useEffect(() => {
        const handleMouseEnter = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target && target.classList?.contains('glossary-term')) {
                const tag = target.getAttribute('data-tag') || '';
                const definition = target.getAttribute('data-definition') || '';
                setPopup({
                    visible: true,
                    x: e.clientX,
                    y: e.clientY,
                    tag,
                    definition
                });
            }
        };

        const handleMouseLeave = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target && target.classList?.contains('glossary-term')) {
                setPopup(prev => ({ ...prev, visible: false }));
            }
        };

        const handleMouseMove = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target && target.classList?.contains('glossary-term') && popup.visible) {
                setPopup(prev => ({
                    ...prev,
                    x: e.clientX,
                    y: e.clientY
                }));
            }
        };

        document.addEventListener('mouseenter', handleMouseEnter, true);
        document.addEventListener('mouseleave', handleMouseLeave, true);
        document.addEventListener('mousemove', handleMouseMove, true);

        return () => {
            document.removeEventListener('mouseenter', handleMouseEnter, true);
            document.removeEventListener('mouseleave', handleMouseLeave, true);
            document.removeEventListener('mousemove', handleMouseMove, true);
        };
    }, [popup.visible]);

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' })
    }, [appStateContext.state.isLoading, currentMessages[(currentMessages.length - 1) > 0 ? currentMessages.length - 1 : 0]]);

    const sendUpdatedMessage = (message: ChatMessage) => {
        const currentConversation = appStateContext.state.currentConversation;

        const sessionId = currentConversation.sessionId;
        const documentId = appStateContext.state.activeDocument?.documentId || null;
        appStateContext.updateChatMessageRequest(sessionId, message, documentId);

        setMessageUpdating(null);
    }
    const handleExportOneSheet = async (exportType: string) => {
        try {
            setDownloadingExportType(exportType); // Disable the button
            const sessionId = appStateContext.state.currentConversation.sessionId;
            if (!sessionId) {
                alert("No session ID found.");
                setDownloadingExportType(null);
                return;
            }
            const abortController = new AbortController();
            await exportChat(exportType, sessionId, abortController.signal);
        } catch (err) {
            alert("Error exporting chat history.");
            console.error(err);
        } finally {
            setDownloadingExportType(null); // Re-enable the button
        }
    };
    return (
        <Box sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 720 },
            mx: "auto",
            boxSizing: "border-box",
        }}>
            {/* Glossary Popup */}
            {popup.visible && (
                <Paper
                    elevation={4}
                    sx={{
                        position: "fixed",
                        left: popup.x + 12,
                        top: popup.y + 12,
                        zIndex: 2000,
                        pointerEvents: "none",
                        p: 1,
                        minWidth: 180,
                        maxWidth: 320,
                        bgcolor: "background.paper",
                        border: "1px solid #ccc",
                    }}
                >
                    <Typography variant="subtitle2" fontWeight={700}>
                        {popup.tag}
                    </Typography>
                    <Typography variant="body2">{popup.definition}</Typography>
                </Paper>
            )}

            {/* Fixed header */}
            <Box sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: 'background.default',
                pb: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                maxWidth: { xs: "100%", sm: 720 },
                mx: "auto",
                boxSizing: "border-box",
            }}>
                <ChevronProgress currentAgent={currentAgent} />
            </Box>

            <Stack
                spacing={4}
                sx={{
                    p: 3,
                    width: "100%",
                    maxWidth: 720,
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            >

                {currentMessages.map((message, index) => {
                    const isLatestAssistant =
                        message.authorRole.label === AUTHOR_ROLE.ASSISTANT &&
                        index === currentMessages.length - 1;

                    if (
                        message.authorRole.label === AUTHOR_ROLE.ASSISTANT
                    ) {
                        // Render export buttons inline, but do NOT mutate message.content
                        let messageContent: React.ReactNode = null;
                        let contentToRender = message.content;
                        // Remove confidence score e.g. [[[[CONFIDENCE:0.95]]]]
                        // contentToRender = contentToRender.replace(/\[\[\[\[CONFIDENCE:[^\]]+\]\]\]\]/g, "");
                        // // Only clean up confidence marker if not loading (i.e., message is complete)
                        // if (!appStateContext.state.isLoading || index !== currentMessages.length - 1) {
                        //     contentToRender = contentToRender.replace(/\[\[\[\[CONFIDENCE:[^\]]+\]\]\]\]/g, "");
                        // }

                        // Apply glossary highlighting
                        contentToRender = highlightGlossaryTerms(contentToRender);

                        // Regex to match "Export to PDF:[exportType]"

                        // Regex to match "Export to PDF:[exportType]"
                        const exportRegex = /Export to PDF:([a-zA-Z0-9_-]+)/g;
                        let lastIndex = 0;
                        const parts: React.ReactNode[] = [];
                        let match: RegExpExecArray | null;

                        while ((match = exportRegex.exec(contentToRender)) !== null) { // <-- use contentToRender here!
                            // Render markdown for text before the match
                            if (match.index > lastIndex) {
                                parts.push(
                                    <ReactMarkdown
                                        key={`text-${lastIndex}`}
                                        remarkPlugins={[remarkGfm]}
                                        rehypePlugins={[rehypeRaw]}
                                        children={DOMPurify.sanitize(contentToRender.slice(lastIndex, match.index), { ALLOWED_TAGS: [...XSSAllowTags, 'span'] })}
                                    />
                                );
                            }
                            const exportType = match[1];
                            const isDownloading = downloadingExportType === exportType;

                            parts.push(
                                <Box
                                    key={`export-${exportType}-${match.index}`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        background: 'background.paper',
                                        borderRadius: '40px',
                                        boxShadow: 'none',
                                        height: '32px', // match your button height
                                        width: 'fit-content',
                                        overflow: 'hidden',
                                        cursor: isDownloading ? 'not-allowed' : 'pointer',
                                        px: 0,
                                        py: 0,
                                        mr: 2, // optional margin-right for spacing
                                        opacity: isDownloading ? 0.7 : 1,
                                        pointerEvents: isDownloading ? 'none' : 'auto',
                                        '&:hover': {
                                            bgcolor: !isDownloading ? '#e50c20' : undefined,
                                        },
                                    }}
                                    onClick={() => {
                                        if (!isDownloading) handleExportOneSheet(exportType);
                                    }}
                                >
                                    <Box
                                        sx={{
                                            background: '#e50c20',
                                            width: '2%',
                                            minWidth: '20px',
                                            height: '100%',
                                            borderRadius: '40px 0 0 40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    />
                                    <Typography
                                        component="button"
                                        className="pdf_export"
                                        sx={{
                                            border: 'none',
                                            outline: 'none',
                                            background: 'transparent',
                                            fontSize: '.95rem',
                                            fontWeight: 600,
                                            pl: 2,
                                            pr: 4,
                                            py: 1.5,
                                            textAlign: 'left',
                                            whiteSpace: 'nowrap',
                                            color: 'text.primary',
                                            cursor: isDownloading ? 'not-allowed' : 'pointer',
                                            userSelect: 'none',
                                            '&:hover': {
                                                bgcolor: !isDownloading ? '#e50c20' : undefined,
                                                color: !isDownloading ? '#fff' : 'custom.constantBlack',
                                            },
                                        }}
                                        disabled={isDownloading}
                                    >
                                        {isDownloading ? "Downloading..." : "Export to PDF"}
                                    </Typography>
                                </Box>
                            );
                            lastIndex = match.index + match[0].length;
                        }
                        // Render markdown for any remaining text after the last match
                        if (lastIndex < contentToRender.length) { // <-- use contentToRender here!
                            parts.push(
                                <ReactMarkdown
                                    key={`text-end-${lastIndex}`}
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                    children={DOMPurify.sanitize(contentToRender.slice(lastIndex), { ALLOWED_TAGS: [...XSSAllowTags, 'span'], ALLOW_DATA_ATTR: true })}
                                />
                            );
                        }                        // If no matches, render the whole string as markdown
                        if (parts.length === 0) {
                            messageContent = (
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                    children={DOMPurify.sanitize(contentToRender, { ALLOWED_TAGS: [...XSSAllowTags, 'span'], ALLOW_DATA_ATTR: true })}
                                />
                            );
                        } else {
                            messageContent = parts;
                        }

                        return (
                            <MuiAiAnswer
                                key={message.messageId}
                                message={{ ...message, content: message.content }}
                                generating={index === currentMessages.length - 1 && appStateContext.state.isLoading && !message.content}
                                showExportButton={false}
                                onExportClick={() => { }}
                                showFooterButtons={isLatestAssistant && showFooterButtons} // <-- combine both conditions
                            >
                                {messageContent}
                            </MuiAiAnswer>
                        );
                    } else if (message.authorRole.label === AUTHOR_ROLE.ERROR) {
                        return (
                            <MuiAiAnswer
                                key={message.messageId}
                                message={{ ...message, content: message.content }}
                                generating={index === currentMessages.length - 1 && appStateContext.state.isLoading && !message.content}
                                showExportButton={false}
                                onExportClick={() => { }}
                                showFooterButtons={false} // <-- combine both conditions
                            >
                                {/* Caution Icon*/}
                                <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    color="#e50c20"
                                />
                                &nbsp;
                                {'There was an error generating a response.'}
                                {currentMessages.length - 1 === index &&
                                    <>
                                        <br />
                                        <br />
                                        <Button
                                            sx={{
                                                backgroundColor: '#e50c20',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#b8091a',
                                                },
                                                display: 'block',
                                                justifySelf: 'right'
                                            }}
                                            onClick={() => {
                                                // Get last user message
                                                const lastUserMessage = currentMessages
                                                    .slice()
                                                    .reverse()
                                                    .find(msg => msg.authorRole.label === AUTHOR_ROLE.USER);
                                                if (lastUserMessage) {
                                                    // Call the retry function with the last user message
                                                    retry(lastUserMessage.content);
                                                }
                                            }}
                                        >
                                            Regenerate Response
                                        </Button>
                                    </>}
                            </MuiAiAnswer>
                        );
                    } else if (message.authorRole.label === AUTHOR_ROLE.USER) {
                        if (message.messageId !== messageUpdating?.messageId) {
                            return (
                                <MuiUserMessage
                                    key={message.messageId}
                                    message={message}
                                    updateMessage={setMessageUpdating}
                                />
                            );
                        } else {
                            return (
                                <MuiDraftMessage
                                    key={message.messageId}
                                    message={message}
                                    cancelUpdate={() => setMessageUpdating(null)}
                                    sendMessage={sendUpdatedMessage}
                                />
                            );
                        }
                    }
                    // Add a fallback return for any other message types
                    return null;
                })}
                <Box
                    ref={chatMessageStreamEnd}
                    sx={{ height: 0, width: 0 }}
                />
            </Stack >
        </Box>
    );
};

export default MuiChatThread;
